import { useRef, useState } from 'react';
import { processImageFile } from '../utils/imageFileUtils';

export default function MagnifiedScalpUpload({ value, onChange, disabled = false }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingPreview, setPendingPreview] = useState(null);

  const handleFile = async (file) => {
    if (!file || disabled || loading) return;

    setError(null);
    setLoading(true);
    try {
      const dataUrl = await processImageFile(file);
      setPendingPreview(dataUrl);
    } catch (err) {
      setError(err?.message || 'Invalid image');
      setPendingPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmPending = () => {
    if (!pendingPreview || disabled) return;
    onChange?.(pendingPreview);
    setPendingPreview(null);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setError(null);
    setPendingPreview(null);
    onChange?.(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayUrl = pendingPreview || value;

  return (
    <div className="magnified-scalp-upload">
      <div className="magnified-scalp-upload__header">
        <h3 className="magnified-scalp-upload__title">Magnified Scalp Photo</h3>
        <p className="magnified-scalp-upload__subtitle">
          Required for magnified scalp analysis on your report. Upload a close-up parted-hair photo.
        </p>
      </div>

      {displayUrl ? (
        <div className="magnified-scalp-upload__preview">
          <img src={displayUrl} alt="Magnified scalp preview" />
          <div className="magnified-scalp-upload__preview-actions">
            {pendingPreview ? (
              <>
                <button
                  type="button"
                  className="magnified-scalp-upload__btn magnified-scalp-upload__btn--secondary"
                  disabled={disabled || loading}
                  onClick={() => {
                    setPendingPreview(null);
                    if (inputRef.current) inputRef.current.value = '';
                  }}
                >
                  Retake
                </button>
                <button
                  type="button"
                  className="magnified-scalp-upload__btn magnified-scalp-upload__btn--secondary"
                  disabled={disabled || loading}
                  onClick={confirmPending}
                >
                  Confirm photo
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="magnified-scalp-upload__btn magnified-scalp-upload__btn--secondary"
                  disabled={disabled || loading}
                  onClick={() => inputRef.current?.click()}
                >
                  Replace
                </button>
                <button
                  type="button"
                  className="magnified-scalp-upload__btn magnified-scalp-upload__btn--ghost"
                  disabled={disabled || loading}
                  onClick={handleRemove}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`magnified-scalp-upload__zone ${dragging ? 'magnified-scalp-upload__zone--drag' : ''} ${loading ? 'magnified-scalp-upload__zone--loading' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files[0]) void handleFile(e.dataTransfer.files[0]);
          }}
          onClick={() => !disabled && !loading && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled && !loading) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-disabled={disabled || loading}
        >
          <p className="magnified-scalp-upload__zone-title">
            {loading ? 'Processing...' : 'Drop image here or click to browse'}
          </p>
          <p className="magnified-scalp-upload__zone-hint">PNG, JPG, WEBP · up to 20MB · min 256×256</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        disabled={disabled || loading}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      {error && <p className="magnified-scalp-upload__error" role="alert">{error}</p>}
    </div>
  );
}
